import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { Accordion, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import ShipAddressList from "../ShipmentProducts/ShipAddressList";
import { truncateString } from "utils/UtilityFuncs";
import CustomerSelectModal from "./CustomerSelectModal";
import AddProductsToShipment from "../AddProductsToShipment";
import { useNavigate, useSearchParams } from "react-router-dom";
import ShipmentTracking from "./ShipmentTracking";
import ShipmentPdfButton from "components/Pdfs/ShipmentPdf/ShipmentPdfButton";

export default function ShipmentDetails({ shipment, refreshShipment }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showQtyModal, setShowQtyModal] = useState(false);
  const [refreshProducts, setRefreshProducts] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState([]);
  const [shipmentQtyByProduct, setShipmentQtyByProduct] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageToView, setImageToView] = useState(null);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState(null);

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [activeFilter, setActiveFilter] = useState(null);

  // Replace expandedAddress & expandedProducts with a single state for active accordion
  const [activeAccordion, setActiveAccordion] = useState([
    "address",
    "products",
  ]);

  // Helper to parse productSizes safely
  const parseSizes = (sizes) => {
    if (Array.isArray(sizes)) return sizes;
    if (typeof sizes === "string") {
      try {
        return JSON.parse(sizes);
      } catch {
        return [];
      }
    }
    return [];
  };

  // Fetch products for shipment
  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const { data } = await API.get(
        "/shipment-products/" + shipment?.shipmentId,
        { headers: authHeader() }
      );
      if (data?.success) {
        setProducts(data?.data || []);
      }
      if (refreshProducts) {
        setRefreshProducts(false);
      }
      setProductsLoading(false);
    } catch (err) {
      setProductsLoading(false);
    }
  };

  // Update Quantity
  const updateQty = async (productId, updatedSizes) => {
    if (!productId || !updatedSizes) return;
    try {
      let _productStatus = getProductStatusLabel(productId);

      let productStatus =
        _productStatus === "Fully Shipped"
          ? "shipped"
          : _productStatus === "Partially Shipped"
          ? "partially_shipped"
          : "pending";

      setProductsLoading(true);
      const product = products.find((p) => p.id === productId);
      const updatedProduct = {
        ...product,
        productSizes: JSON.stringify(updatedSizes),
        productStatus: productStatus?.toLowerCase(),
      };
      const { data } = await API.patch("/shipment-product/", updatedProduct, {
        headers: authHeader(),
      });
      if (data?.success) {
        setShowQtyModal(false);
        setSelectedProduct(null);
        fetchProducts();
      }
      setProductsLoading(false);
    } catch (err) {
      setProductsLoading(false);
    }
  };

  // Delete Product
  const deleteProduct = async (productId) => {
    try {
      setProductsLoading(true);
      const { data } = await API.delete("/shipment-product/" + productId, {
        headers: authHeader(),
      });
      if (data?.success) {
        await fetchProducts();
      }
      setProductsLoading(false);
    } catch (err) {
      setProductsLoading(false);
    }
  };

  // Save shipment qty for a single product
  const handleSaveProduct = (productId) => {
    // You may want to call updateQty with the productId and shipmentQtyByProduct[productId]
    updateQty(productId, shipmentQtyByProduct[productId]);
  };

  // Save all products
  const handleSaveAll = (e) => {
    e.preventDefault();
    products.forEach((prod) => {
      updateQty(prod.id, shipmentQtyByProduct[prod.id]);
    });
  };

  // Update shipment qty for a product size
  const handleQtyChange = (productId, sizeId, value) => {
    setShipmentQtyByProduct((prev) => {
      const sizes = prev[productId]?.map((size) =>
        size.id === sizeId ? { ...size, shipmentQty: Number(value) } : size
      );
      return { ...prev, [productId]: sizes };
    });
  };

  // Helper to check shipped status for color
  const getProductStatus = (productId) => {
    const sizes = shipmentQtyByProduct[productId] || [];
    if (sizes.length === 0) return "secondary";
    const allShipped = sizes.every((s) => s.shipmentQty >= s.quantity);
    const someShipped = sizes.some((s) => s.shipmentQty > 0);
    if (allShipped) return "success";
    if (someShipped) return "warning";
    return "secondary";
  };

  // Helper to get status label for each product
  const getProductStatusLabel = (productId) => {
    const sizes = shipmentQtyByProduct[productId] || [];
    if (sizes.length === 0) return "Pending";
    const allShipped = sizes.every(
      (s) => s.shipmentQty >= s.quantity && s.quantity > 0
    );
    const someShipped = sizes.some((s) => s.shipmentQty > 0);
    if (allShipped) return "Fully Shipped";
    if (someShipped) return "Partially Shipped";
    return "Pending";
  };

  const handleAccordionToggle = (productId) => {
    setExpandedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  // Set all sizes of a product to shipped qty
  const handleShipAllSizes = (productId) => {
    setShipmentQtyByProduct((prev) => {
      const sizes = prev[productId]?.map((size) => {
        const maxQty = Number(size.quantity);
        return { ...size, shipmentQty: maxQty };
      });
      return { ...prev, [productId]: sizes };
    });
  };

  // Set a single size to shipped qty when checkbox is checked
  const handleSizeCheckbox = (productId, sizeId, checked) => {
    setShipmentQtyByProduct((prev) => {
      const sizes = prev[productId]?.map((size) =>
        size.id === sizeId
          ? { ...size, shipmentQty: checked ? Number(size.quantity) : 0 }
          : size
      );
      return { ...prev, [productId]: sizes };
    });
  };

  // Save updated address
  const handleSaveAddress = async () => {
    try {
      setProductsLoading(true);
      const body = {
        id: shipment.id,
        shippingAddress: JSON.stringify(addressForm),
      };
      const { data } = await API.patch("/sales/shipment", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setEditingAddress(false);
      }
      setProductsLoading(false);
    } catch (err) {
      setProductsLoading(false);
    }
  };

  /*   Customer Select Functions
   **************** */
  const handleCustomerSelect = async (customer) => {
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/sales/shipment",
        {
          id: shipment.id,
          customerId: customer["customer.customerId"],
        },
        { headers: authHeader() }
      );

      if (data?.success) {
        // setSelectedCustomer(customer);
        await refreshShipment(shipment.shipmentId);
        setLoading(false);
        dispatch(
          setNotification({
            message: "Customer updated successfully",
            type: "success",
          })
        );
      } else {
        setLoading(false);
        dispatch(
          setNotification({
            message: "Failed to update customer",
            type: "error",
          })
        );
      }
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  const handleViewCustomer = (customerId) => {
    navigate(`/customer/details/${customerId}`);
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      fetchProducts();
    }, 50);
    return () => clearTimeout(timeout);
  }, [refreshProducts, shipment]);

  // Set selected customer if available in shipment
  useEffect(() => {
    if (shipment?.customer) {
      setSelectedCustomer(shipment.customer);
    }
  }, [shipment]);

  // Initialize shipment qty for each product/size when products change
  useEffect(() => {
    if (products.length > 0) {
      const initial = {};
      products.forEach((prod) => {
        const shipmentSizes = parseSizes(prod.productSizes);
        initial[prod.id] = shipmentSizes.map((size) => ({
          ...size,
          shipmentQty: size.shipmentQty || 0,
        }));
      });
      setShipmentQtyByProduct(initial);
    }
  }, [products]);

  // Parse shipping address from shipment
  useEffect(() => {
    if (shipment?.shippingAddress) {
      let addr = shipment.shippingAddress;
      if (typeof addr === "string") {
        try {
          addr = JSON.parse(addr);
        } catch {
          addr = {
            addressCompany: "",
            addressContact: "",
            addressLine1: "",
            addressLine2: "",
            addressCity: "",
            addressState: "",
            addressZipCode: "",
            addressCountry: "",
            description: "",
          };
        }
      }

      setAddressForm(addr);
    }
  }, [shipment?.shippingAddress]);

  useEffect(() => {
    // check if searchParams has shipment-id, if yes then remove it.
    if (searchParams.get("shipment-id")) {
      setSearchParams({});
    }
  }, [searchParams]);

  // Calculate dynamic height for products accordion
  // Assume header+address accordion take ~340px, adjust as needed for your layout

  // Helper for toggling accordion keys
  const toggleAccordion = (key) => {
    setActiveAccordion((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  return (
    <>
      {/* Accordions for Address/Tracking and Shipment Products */}
      <div className="mb-4">
        {loading && <Spinner />}
        <Accordion
          className="bg-white shadow-sm rounded-3 mb-2"
          activeKey={activeAccordion}
          alwaysOpen={true}
        >
          {/* Shipping Address & Tracking Accordion */}
          <Accordion.Item eventKey="address">
            <Accordion.Header onClick={() => toggleAccordion("address")}>
              <div className="d-flex justify-content-between align-items-center w-100">
                <div
                  className="fw-bold d-flex align-items-center gap-1"
                  style={{ fontSize: "1.2rem" }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      handleViewCustomer(selectedCustomer?.customerId);
                    }}
                    className="btn btn-info btn-sm px-2 me-1"
                  >
                    View
                  </button>{" "}
                  <span className="me-2 text-dark">
                    {selectedCustomer ? (
                      <>
                        <span>C-{selectedCustomer?.customerId}</span>
                        {" - "} {selectedCustomer?.customerName} ({" "}
                        <span>
                          {selectedCustomer?.accountPayableEmail
                            ? selectedCustomer?.accountPayableEmail
                            : "No default email"}
                        </span>{" "}
                        )
                      </>
                    ) : (
                      "None"
                    )}
                  </span>
                </div>
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="d-flex align-items-center gap-2 fs-5"
                >
                  <CustomerSelectModal
                    shipment={shipment}
                    selectedCustomer={selectedCustomer}
                    handleCustomerSelect={handleCustomerSelect}
                    refreshShipment={refreshShipment}
                    activeFilter={activeFilter}
                    setActiveFilter={setActiveFilter}
                  />
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body className="pt-1">
              <div className="row">
                {/* Shipping Address */}
                <div className="col-md-7 border-end pe-4">
                  <div
                    className="border-bottom border-3 pe-3 mb-2 pb-1 d-flex align-items-center justify-content-between"
                    style={
                      {
                        // width: "fit-content",
                      }
                    }
                  >
                    <h5 className="fw-bold mb-1">Shipping Address</h5>
                    <div className="d-flex align-items-center justify-content-end gap-1">
                      <ShipAddressList
                        shipment={shipment}
                        products={products}
                        addressForm={addressForm}
                        setAddressForm={setAddressForm}
                      />
                      {!editingAddress ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setEditingAddress(true)}
                        >
                          Edit
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => {
                              setEditingAddress(false);
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={handleSaveAddress}
                            disabled={productsLoading}
                          >
                            Save
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <form>
                    {!editingAddress ? (
                      <>
                        <div className="row">
                          <div className="col-4 fw-bold"> Contact</div>
                          <div className="col-8">
                            {addressForm?.addressContact}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4 fw-bold"> Company</div>
                          <div className="col-8">
                            {addressForm?.addressCompany}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4 fw-bold">Address Line 1</div>
                          <div className="col-8">
                            {addressForm?.addressLine1}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4 fw-bold">Address Line 2</div>
                          <div className="col-8">
                            {addressForm?.addressLine2}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4 fw-bold">
                            City | State | Zip
                          </div>
                          <div className="col-8">
                            {addressForm?.city} | {addressForm?.state} |{" "}
                            {addressForm?.zip}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4 fw-bold">Country</div>
                          <div className="col-8">{addressForm?.country}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="row g-2">
                          <div className="col-md-6">
                            <label className="form-label">Company</label>
                            <input
                              type="text"
                              className="form-control"
                              value={addressForm?.addressCompany || ""}
                              onChange={(e) =>
                                setAddressForm({
                                  ...addressForm,
                                  addressCompany: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Contact</label>
                            <input
                              type="text"
                              className="form-control"
                              value={addressForm?.addressContact || ""}
                              onChange={(e) =>
                                setAddressForm({
                                  ...addressForm,
                                  addressContact: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Address Line 1</label>
                            <input
                              type="text"
                              className="form-control"
                              value={
                                addressForm?.addressLine1 ||
                                addressForm?.addressLine1 ||
                                ""
                              }
                              onChange={(e) =>
                                setAddressForm({
                                  ...addressForm,
                                  addressLine1: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Address Line 2</label>
                            <input
                              type="text"
                              className="form-control"
                              value={addressForm?.addressLine2 || ""}
                              onChange={(e) =>
                                setAddressForm({
                                  ...addressForm,
                                  addressLine2: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">City</label>
                            <input
                              type="text"
                              className="form-control"
                              value={
                                addressForm?.city ||
                                addressForm?.addressCity ||
                                ""
                              }
                              onChange={(e) =>
                                setAddressForm({
                                  ...addressForm,
                                  city: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">State</label>
                            <input
                              type="text"
                              className="form-control"
                              value={
                                addressForm?.state ||
                                addressForm?.addressState ||
                                ""
                              }
                              onChange={(e) =>
                                setAddressForm({
                                  ...addressForm,
                                  state: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Zip</label>
                            <input
                              type="text"
                              className="form-control"
                              value={
                                addressForm?.zip ||
                                addressForm?.addressZipCode ||
                                ""
                              }
                              onChange={(e) =>
                                setAddressForm({
                                  ...addressForm,
                                  zip: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">Country</label>
                            <input
                              type="text"
                              className="form-control"
                              value={
                                addressForm?.country ||
                                addressForm?.addressCountry ||
                                ""
                              }
                              onChange={(e) =>
                                setAddressForm({
                                  ...addressForm,
                                  country: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </form>
                </div>
                <div className="col-md-5 ps-4">
                  {/* Tracking Number */}
                  <ShipmentTracking
                    shipment={shipment}
                    products={products}
                    refreshShipment={refreshShipment}
                  />
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        {/* ******************** Shipment Products ************************* */}
        <Accordion
          className="bg-white shadow-sm rounded-3"
          activeKey={activeAccordion}
          alwaysOpen={true}
        >
          {/* Products Accordion */}
          <Accordion.Item eventKey="products">
            <Accordion.Header onClick={() => toggleAccordion("products")}>
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="fw-bold" style={{ fontSize: "1.1rem" }}>
                  Shipment Products
                </div>
                <div
                  className="me-3 d-flex align-items-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ShipmentPdfButton
                    shipment={shipment}
                    products={products}
                    btnClasses="btn btn-sm btn-success text-nowrap me-2"
                  />
                  {selectedCustomer && (
                    <AddProductsToShipment
                      shipment={shipment}
                      selectedCustomer={selectedCustomer}
                      activeFilter={activeFilter || "pending-processing"}
                      refreshProducts={fetchProducts}
                      refreshShipment={refreshShipment}
                      setActiveFilter={setActiveFilter}
                      // activeFilter={activeFilter}
                      // refreshCustomers={refreshCustomers}
                    />
                  )}
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              {/* Shipment Products Summary */}
              <div className="d-flex align-items-center justify-content-between gap-4 my-2">
                <div className="d-flex align-items-center gap-4">
                  {productsLoading && <Spinner />}
                  <div>
                    <span className="fw-bold me-1">
                      Total Shipment Products:
                    </span>
                    {products.length}
                  </div>
                  <div>
                    <span className="fw-bold me-1">Total Products Qty:</span>
                    {products.reduce(
                      (acc, p) => acc + (p.WOProduct?.totalQuantity || 0),
                      0
                    )}
                  </div>
                  <div>
                    <span className="fw-bold me-1">Total Shipment Qty:</span>
                    {products.reduce((acc, p) => acc + (p.shipmentQty || 0), 0)}
                  </div>
                  <div>
                    <span className="fw-bold me-1">
                      Remaining Products Qty:
                    </span>
                    {products.reduce(
                      (acc, p) =>
                        acc +
                        ((p.WOProduct?.totalQuantity || 0) -
                          (p.shipmentQty || 0)),
                      0
                    )}
                  </div>
                </div>
                <div className="text-end ">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveAll}
                    disabled={products.length === 0}
                  >
                    Save All
                  </button>
                </div>
              </div>
              <div
                style={{
                  height: activeAccordion.includes("address") ? "30vh" : "58vh",
                  overflowY: "auto",
                }}
              >
                <Accordion className="border-0" activeKey={expandedProducts}>
                  {/* REMOVE: getFilteredProducts usage, use products directly */}
                  {products.length === 0 && !productsLoading && (
                    <div className="alert alert-info mt-3">
                      No products found for this shipment
                    </div>
                  )}
                  {products.map((product) => {
                    const originalSizes = parseSizes(
                      product.WOProduct?.productSizes
                    );
                    return (
                      <Accordion.Item
                        className="border-0 shadow-sm"
                        eventKey={product.id}
                        key={product.id}
                      >
                        <Accordion.Header
                          onClick={() => handleAccordionToggle(product.id)}
                          style={{
                            borderRadius: 6,
                            padding: 0,
                          }}
                          className={`border mt-2  bg-${
                            getProductStatus(product.id) !== "secondary"
                              ? `${getProductStatus(product.id)} bg-opacity-25`
                              : "light"
                          }`}
                        >
                          <div
                            className="d-flex align-items-center w-100"
                            style={{ gap: 0 }}
                          >
                            <div
                              style={{
                                width: 50,
                                minWidth: 50,
                                height: 50,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setImageToView(
                                  product?.WOProduct?.productImage
                                );
                                setShowImageModal(true);
                              }}
                            >
                              {product?.WOProduct?.productImage ? (
                                <ImageWIthSignedUrl
                                  path={product.WOProduct.productImage}
                                  width={40}
                                  height={40}
                                  style={{
                                    objectFit: "cover",
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 50,
                                    height: 60,
                                    background: "#f0f0f0",
                                    borderRadius: 6,
                                  }}
                                />
                              )}
                            </div>
                            <div
                              style={{
                                width: 150,
                                minWidth: 100,
                                paddingLeft: 12,
                              }}
                            >
                              <strong>
                                {product?.WOProduct?.productStyle}
                              </strong>
                              <br />
                              <small className="mt-2">W-{product?.woId}</small>
                            </div>
                            <div
                              style={{
                                width: 250,
                                minWidth: 200,
                                paddingLeft: 12,
                              }}
                            >
                              {truncateString(
                                product?.WOProduct?.productDescription,
                                30
                              )}
                            </div>
                            <div style={{ width: 120, minWidth: 120 }}>
                              {product?.WOProduct?.productTypes?.productTypes ||
                                "-"}
                            </div>
                            <div style={{ width: 150, minWidth: 100 }}>
                              {product?.WOProduct?.productColor || "-"}
                            </div>
                            <div
                              style={{
                                flex: 1,
                                width: 80,
                                minWidth: 60,
                                textAlign: "center",
                              }}
                            >
                              <span
                                className={`badge py-1 fs-6 bg-${getProductStatus(
                                  product.id
                                )}`}
                              >
                                {getProductStatusLabel(product.id)}
                              </span>
                            </div>
                            <div
                              style={{
                                minWidth: 40,
                                textAlign: "right",
                                paddingRight: 15,
                              }}
                            >
                              <button
                                className="btn btn-sm btn-success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveProduct(product.id);
                                }}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger ms-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteProduct(product.id);
                                }}
                                disabled={productsLoading}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body className=" bg-light bg-opacity-75 shadow-sm mb-2">
                          {/* Ship All Button above pills */}
                          <div className="mb-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleShipAllSizes(product.id)}
                              disabled={productsLoading}
                            >
                              Ship All
                            </button>
                          </div>
                          {/* Pills for sizes */}
                          <div
                            className="d-flex flex-row flex-wrap gap-2"
                            style={{ marginBottom: 8 }}
                          >
                            {(shipmentQtyByProduct[product.id] || []).map(
                              (size, idx) => {
                                const origSize = originalSizes.find(
                                  (os) => os.id === size.id
                                );
                                const maxQty = Number(
                                  origSize ? origSize.quantity : size.quantity
                                );
                                return (
                                  <div
                                    key={size.id}
                                    className="d-flex align-items-center border rounded-pill px-2 py-1 bg-white"
                                    style={{
                                      minWidth: 110,
                                      marginBottom: 4,
                                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                    }}
                                  >
                                    {/* Checkbox */}
                                    <input
                                      type="checkbox"
                                      className="form-check-input me-1"
                                      checked={size.shipmentQty === maxQty}
                                      onChange={(e) =>
                                        handleSizeCheckbox(
                                          product.id,
                                          size.id,
                                          e.target.checked
                                        )
                                      }
                                      style={{ cursor: "pointer" }}
                                    />
                                    {/* Size Name */}
                                    <span className="fw-bold me-1">
                                      {size.sizeName || size.size}
                                    </span>
                                    {/* Qty shipped/total */}
                                    <span className="badge bg-secondary me-1">
                                      {size.shipmentQty}/{maxQty}
                                    </span>
                                    {/* Qty input */}
                                    <input
                                      type="number"
                                      min={0}
                                      max={maxQty}
                                      value={size.shipmentQty}
                                      onChange={(e) => {
                                        let val = Math.max(
                                          0,
                                          Math.min(
                                            Number(e.target.value),
                                            maxQty
                                          )
                                        );
                                        handleQtyChange(
                                          product.id,
                                          size.id,
                                          val
                                        );
                                      }}
                                      className="form-control form-control-sm"
                                      style={{ width: 70, marginLeft: 2 }}
                                    />
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>

      <Modal show={showQtyModal} onHide={() => setShowQtyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Product Quantity</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={updateQty}>
            <div>
              <label htmlFor="quantity">
                Shipment Quantity
                <small>
                  (Max: {selectedProduct?.WOProduct?.totalQuantity || 0})
                </small>
              </label>
              <input
                type="number"
                id="quantity"
                className="form-control"
                value={selectedProduct?.shipmentQty || 0}
                max={selectedProduct?.WOProduct?.totalQuantity || 0}
                onChange={(e) =>
                  setSelectedProduct({
                    ...selectedProduct,
                    shipmentQty: e.target.value,
                  })
                }
              />
            </div>
            <div className="border-top border-1 mt-3 pt-2 text-end">
              <button
                type="button"
                className="btn btn-secondary btn-sm ms-1"
                onClick={() => {
                  setShowQtyModal(false);
                  setSelectedProduct(null);
                }}
              >
                Close
              </button>
              <button className="btn btn-primary btn-sm ms-1" type="submit">
                Save Changes
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="xl"
        centered
        contentClassName="bg-light custom-shadow-sm custom-border-radius"
      >
        <Modal.Header closeButton>
          <Modal.Title>Product Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="">
          <div className="text-center">
            {imageToView && (
              <ImageWIthSignedUrl
                path={imageToView}
                width={600}
                height={400}
                style={{ objectFit: "contain" }}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowImageModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
